<script setup lang="ts">
  import UserAvatar from '@/components/app/users/UserAvatar.vue'
  import PDFViewer from '@/components/features/pdf/pdfViewer.vue'
  import {
    AugmentationVariantEnum,
    type CustomerCaseDataChange as CaseDataChange,
    ChangeSourceEnum,
    type CustomerEntityChange as EntityChange
  } from '@/types/generated/types.gen'
  import {
    Calendar,
    FileText,
    Mail,
    MessageCircleQuestion,
    MessageSquare,
    Phone
  } from 'lucide-vue-next'
  import { computed, defineProps, type PropType, ref } from 'vue'

  const props = defineProps({
    change: {
      type: Object as PropType<EntityChange | CaseDataChange | null>,
      required: false
    }
  })

  const isPdfOpen = ref(false)
  const pdfUrl = ref('')
  const initialPage = computed(() => {
    if (props.change?.ai_augmentation?.referenced_document_pages?.length > 0) {
      return props.change.ai_augmentation.referenced_document_pages[0]
    }
    return 1
  })

  function openPdf() {
    if (props.change?.ai_augmentation?.document) {
      pdfUrl.value = props.change.ai_augmentation.document.url
      isPdfOpen.value = true
    }
  }
</script>
<template>
  <div v-if="change">
    <span
      v-if="change.change_source === ChangeSourceEnum.USER"
      class="flex inline-flex items-center gap-2"
    >
      <UserAvatar
        :user="change.user"
        size="26px"
      />
      {{ change.user.first_name }} {{ change.user.last_name }}
    </span>
    <span
      v-else-if="change.change_source === ChangeSourceEnum.QUESTIONNAIRE"
      class="flex inline-flex items-center gap-2"
    >
      <MessageCircleQuestion class="h-4 w-4" />
      Fragebogen
    </span>
    <span
      v-else-if="change.change_source === ChangeSourceEnum.APPOINTMENT"
      class="flex inline-flex items-center gap-2"
    >
      <Calendar class="h-4 w-4" />
      Terminbuchung
    </span>
    <span
      v-else-if="change.change_source === ChangeSourceEnum.AI"
      class="flex inline-flex items-center gap-2"
    >
      <span
        v-if="
          change.ai_augmentation.augmentation_variant === AugmentationVariantEnum.DOCUMENT_ANALYSIS
        "
        class="flex inline-flex items-center gap-2"
      >
        <FileText class="h-4 w-4" />
        <span
          v-if="change.ai_augmentation"
          class="badge bg-purple"
        >
          KI
        </span>
        <button
          class="inline-flex cursor-pointer items-center space-x-1 text-purple-700"
          @click="openPdf"
        >
          <span>
            <span v-if="change.ai_augmentation.document.filename.length > 20">
              {{ change.ai_augmentation.document.filename.slice(0, 12) }}[…].pdf
            </span>
            <span v-else>
              {{ change.ai_augmentation.document.filename }}
            </span>
            <span
              v-for="page in change.ai_augmentation.referenced_document_pages"
              :key="page"
              class="ml-1 inline-flex items-center justify-center rounded-full bg-purple-100 px-2 py-0.5 text-xs
                font-medium text-purple-700"
              :title="`Seite ${page}`"
            >
              S. {{ page }}
            </span>
          </span>
        </button>
      </span>
      <span
        v-else-if="
          change.ai_augmentation.augmentation_variant === AugmentationVariantEnum.EMAIL_EXTRACTION
        "
        class="flex inline-flex items-center gap-2"
      >
        <Mail class="h-4 w-4" />
        <span
          v-if="change.ai_augmentation"
          class="badge bg-purple"
        >
          KI
        </span>
        E-Mail
      </span>
      <span
        v-else-if="change.ai_augmentation.augmentation_variant === AugmentationVariantEnum.CALL"
        class="flex inline-flex items-center gap-2"
      >
        <Phone class="h-4 w-4" />
        <span
          v-if="change.ai_augmentation"
          class="badge bg-purple"
        >
          KI
        </span>
        Anruf
      </span>
      <span
        v-else-if="change.ai_augmentation.augmentation_variant === AugmentationVariantEnum.CHATBOT"
        class="flex inline-flex items-center gap-2"
      >
        <MessageSquare class="h-4 w-4" />
        <span
          v-if="change.ai_augmentation"
          class="badge bg-purple"
        >
          KI
        </span>
        Chatbot
      </span>
    </span>
    <span v-else-if="change.change_source === ChangeSourceEnum.SYSTEM">SYSTEM</span>
    <PDFViewer
      v-if="change?.ai_augmentation?.document"
      v-model:isOpen="isPdfOpen"
      v-model:url="pdfUrl"
      :filename="change.ai_augmentation.document.filename"
      :initial-page="initialPage"
      :scale="1.0"
      :document="change.ai_augmentation.document"
    />
  </div>
</template>
